import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Two different animals behind one screen.
//
// A storeroom's stock is a stored figure kept by the inventory ledger, so it is
// read straight from inventory_stock. A van's is DERIVED on every request from
// its baseline count plus receipts minus usage — there is no stored number, so
// it cannot be filtered or paged in SQL and comes back whole.

export function useStorerooms() {
  return useQuery({
    queryKey: ['stockLevels', 'storerooms'],
    queryFn: async () => (await api.get('/api/inventory/storerooms')).data.storerooms,
    staleTime: 300000,
  });
}

export function useCountedVans() {
  return useQuery({
    queryKey: ['stockLevels', 'vans'],
    queryFn: async () => (await api.get('/api/inventory/vans')).data.vans,
    staleTime: 300000,
  });
}

// group=1 folds interchangeable cable codes (Excel/PPC of a length, the old and
// current inner-cable codes) into one line, the same curated groups goods
// receiving uses to accept a substitute delivery.
export function useStoreroomStock(storeroomId, { q, inStock, group } = {}) {
  const p = new URLSearchParams();
  if (storeroomId) p.set('storeroom_id', storeroomId);
  if (q) p.set('q', q);
  if (inStock) p.set('in_stock', '1');
  if (group) p.set('group', '1');
  return useQuery({
    queryKey: ['stockLevels', 'storeroom', String(storeroomId), q || '', inStock ? '1' : '', group ? 'g' : ''],
    queryFn: async () => (await api.get(`/api/inventory?${p.toString()}`)).data.items,
    enabled: !!storeroomId,
    staleTime: 0,
  });
}

// Correcting stock. A storeroom posts a signed 'correction' through the
// inventory ledger; a van has no stored balance, so its correction is recorded
// as its own term in the derivation. Both insist on a reason.
export function useAdjustStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ mode, storeroomId, registration, productId, stockItemId, newQty, reason }) => {
      const url = mode === 'storeroom'
        ? `/api/inventory/storeroom/${storeroomId}/adjust`
        : `/api/inventory/van/${encodeURIComponent(registration)}/adjust`;
      const body = mode === 'storeroom'
        ? { product_id: productId, new_qty: newQty, reason }
        : { stock_item_id: stockItemId, new_qty: newQty, reason };
      return (await api.post(url, body)).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stockLevels'] }),
  });
}

export function useVanStock(registration, { q, inStock, group } = {}) {
  const p = new URLSearchParams();
  if (q) p.set('q', q);
  if (inStock) p.set('in_stock', '1');
  if (group) p.set('group', '1');
  return useQuery({
    queryKey: ['stockLevels', 'van', registration || '', q || '', inStock ? '1' : '', group ? 'g' : ''],
    queryFn: async () => (await api.get(
      `/api/inventory/van/${encodeURIComponent(registration)}?${p.toString()}`)).data,
    enabled: !!registration,
    staleTime: 0,
  });
}

// Where a line's figure came from. Only fetched when the history is opened.
export function useStoreroomHistory(storeroomId, codes) {
  const list = (codes || []).join(',');
  return useQuery({
    queryKey: ['stockLevels', 'history', String(storeroomId || ''), list],
    queryFn: async () => (await api.get('/api/inventory/history', {
      params: { storeroom_id: storeroomId, codes: list },
    })).data,
    enabled: Boolean(storeroomId && list),
    staleTime: 0,
  });
}

// A van line's movements since its baseline count - the same data Stock Check
// shows, newest 200.
export function useVanItemHistory(registration, stockItemId) {
  return useQuery({
    queryKey: ['stockLevels', 'vanHistory', registration || '', String(stockItemId || '')],
    queryFn: async () => (await api.get(
      `/api/van-requests/reconcile/${encodeURIComponent(registration)}/item/${stockItemId}`,
      { params: { limit: 200 } },
    )).data,
    enabled: Boolean(registration && stockItemId),
    staleTime: 0,
  });
}
